import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { Observable, map } from 'rxjs'

import { ApiHttpService } from '@flowx/designer/shared/api/api-http.service'

import {
  DocumentContextReference,
  InitialMessageRequest,
  InitialMessageResponse,
  Message,
  SendFeedbackPayload,
} from '../../../common/ai.dictionary'

export type GenerateBusinessRulePayload = {
  question: string
  language: string
  context?: AiBusinessRuleAnswer | null
  datasource: string
  session_id?: string
  skip_greeting?: boolean
}

export type GenerateBusinessRuleResponse = {
  question: string
  chat: string
  answer?: AiBusinessRuleAnswer
  datasource: string
  language: string
  context?: string
  session_id?: string
  request_id?: string
}

export type GenerateBusinessRuleFromDocumentResponse = {
  error_message: string
  response: string
  promptValue: string
}

export type AiBusinessRuleAnswer = {
  code: string | null
  explanation: string | null
  language: string
  test_data: string | null
  errors?: string
}

export type ExplainBusinessRulePayload = {
  code_sequence: string
  full_code: string
  session_id?: string
  datasource: string
}

export type ExplainBusinessRuleResponse = {
  code_sequence: string
  chat: string
  datasource: string
  answer: string
  session_id: string
  request_id: string
}

export type ImproveCodePayload = {
  code_sequence: string
  full_code: string
  datasource: string
  language: string
}

export type ImproveCodeResponse = {
  answer: string
  code_sequence: string
  datasource: string
  error_message: string
  language: string
  explanation: string
  request_id: string
  session_id: string
}

export type AddCommentPayload = ImproveCodePayload

export type AddCommentResponse = Omit<ImproveCodeResponse, 'explanation'>

export type FixBusinessRuleWarningsPayload = {
  code_sequence: string
  datasource: any // TODO: add type
  full_code: string
  errors: string[]
}

export type FixWarningsBusinessRuleResponse = {
  answer: string
  code_sequence: string
  datasource: string
  error_message: string
  language: string
  request_id: string
  session_id: string
}

export type GenerateExpressionPayload = {
  question: string
  datasource: string
  session_id: string | null
}

export type GenerateExpressionResponse = {
  question: string
  answer: string
  chat: string
  datasource: string
  session_id: string
  request_id: string
  error_message: string | null
}

export type GenerateGatewayConditionPayload = {
  question: string
  datasource: string
  session_id: string | null
  skip_greeting?: boolean
  outgoing_paths?: { nodeId: string; label: string }[]
}

export type GatewayConditionItem = {
  target_node_id: string
  target_node_name: string
  expression: string
  type: 'if' | 'else if' | 'else'
  order: number
}

export type GenerateGatewayConditionResponse = {
  conditions: GatewayConditionItem[]
  language: string
  explanation: string
  reasoning: string
  session_id?: string
  request_id?: string
}

@Injectable({ providedIn: 'root' })
export class AiDeveloperAPI extends ApiHttpService {
  #developer = `ai-engine/agents/developer`

  constructor() {
    super(inject<string>('BASE_URL' as any), inject(HttpClient))
  }

  initialMessage(payload: InitialMessageRequest): Observable<InitialMessageResponse> {
    return this.post(`${this.#developer}/initial`, payload)
  }

  generateBusinessRule(
    payload: Omit<GenerateBusinessRulePayload, 'processId'>
  ): Observable<GenerateBusinessRuleResponse> {
    const { datasource, language, question, context, session_id } = payload

    return this.post(`${this.#developer}/json`, {
      datasource,
      language,
      question,
      context,
      session_id,
    }).pipe(
      map((response: GenerateBusinessRuleResponse) => {
        if (!response.answer.code) {
          throw new Error(
            response.answer.explanation ||
              `It's unrelated with the defined process and associated datasource`
          )
        }
        return response
      })
    )
  }

  generate(payload: GenerateBusinessRulePayload): Observable<GenerateBusinessRuleResponse> {
    const { language, question, context, session_id, datasource } = payload

    return this.generateBusinessRule({
      question,
      language,
      datasource,
      context,
      session_id,
    })
  }

  extractBusinessRuleFromDocument(
    documentContextReference: DocumentContextReference
  ): Observable<GenerateBusinessRuleFromDocumentResponse> {
    const {
      documentId: collection_db,
      name: br_key,
      session_id: session_id,
    } = documentContextReference

    return this.post(`${this.#developer}/br/extract`, { collection_db, br_key, session_id })
  }

  extractBusinessRuleFromDocumentNew(
    documentContextReference: DocumentContextReference
  ): Observable<GenerateBusinessRuleFromDocumentResponse> {
    const {
      documentId: collection_db,
      name: br_key,
      session_id: session_id,
      value: value,
      description: description,
    } = documentContextReference

    return this.post(`${this.#developer}/br/extract`, {
      collection_db,
      br_key,
      session_id,
      value,
      description,
    })
  }

  extractScreenExpressionFromDocumentNew(
    documentContextReference: DocumentContextReference
  ): Observable<GenerateBusinessRuleFromDocumentResponse> {
    const {
      documentId: collection_db,
      name: br_key,
      session_id: session_id,
      value: value,
      description: description,
    } = documentContextReference

    return this.post(`${this.#developer}/scr/extract`, {
      collection_db,
      br_key,
      session_id,
      value,
      description,
    })
  }

  explain(payload: ExplainBusinessRulePayload): Observable<ExplainBusinessRuleResponse> {
    return this.post(`${this.#developer}/explain/json`, payload)
  }

  improveCode(payload: ImproveCodePayload): Observable<ImproveCodeResponse> {
    return this.post(`${this.#developer}/improve/json`, payload)
  }

  addComment(payload: AddCommentPayload): Observable<AddCommentResponse> {
    return this.post(`${this.#developer}/document/json`, payload)
  }

  fixWarnings(
    payload: FixBusinessRuleWarningsPayload
  ): Observable<FixWarningsBusinessRuleResponse> {
    const { code_sequence, datasource, full_code, errors } = payload

    return this.post(`${this.#developer}/fix/json`, {
      datasource: JSON.stringify(datasource ?? {}),
      code_sequence,
      full_code,
      errors: JSON.stringify(errors ?? []),
    })
  }

  generateExpression(payload: GenerateExpressionPayload): Observable<GenerateExpressionResponse> {
    const { question, datasource, session_id } = payload
    return this.post(`${this.#developer}/expression/json`, {
      question,
      datasource,
      session_id,
    })
  }

  generateGatewayConditionStream(
    payload: GenerateGatewayConditionPayload
  ): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#developer}/chat`, {
      ...payload,
      action: 'gateway_condition',
      language: 'javascript',
    }).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)
          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  sendFeedback(payload: SendFeedbackPayload): Observable<string> {
    const { reason, feedbackType, message, requestId, sessionId } = payload

    return this.post(`${this.#developer}/feedback`, {
      feedback: {
        reason,
        response: feedbackType,
        user_message: message,
      },
      request_id: requestId,
      session_id: sessionId,
    })
  }

  getSSEActions(payload: InitialMessageRequest): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#developer}/actions`, payload).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  getSSEGenerateBusinessRule(
    payload: GenerateBusinessRulePayload
  ): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#developer}/chat`, payload).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  getSSEGenerateReusableFunction(
    payload: GenerateReusableFunctionPayload
  ): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#developer}/chat`, {
      ...payload,
      action: 'reusable_function',
    }).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)
          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  getSSEGenerateCustomComponent(
    payload: GenerateCustomComponentPayload
  ): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#developer}/chat`, {
      ...payload,
      action: 'custom_component',
    }).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)
          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }
}

export type GenerateReusableFunctionPayload = {
  question: string
  language: string
  context?: { code: string | null } | null
  session_id?: string
  skip_greeting?: boolean
  function_metadata: {
    name: string
    description: string
    inputParamsType: string
    outputParamsType: string
    input_slices?: any[]
    output_slices?: any[]
  }
}

export type GenerateCustomComponentPayload = {
  question: string
  context?: {
    sourceCode: string | null
    styles: string | null
    packageJson: string | null
  } | null
  session_id?: string
  skip_greeting?: boolean
  component_metadata: {
    name: string
    uniqueIdentifier: string
    available_libraries: string
    jsx_filename: string
    css_filename: string
  }
}
